/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import * as React from 'react';
import { useI18n } from '@oneaudi/i18n-context';
import { Headline, Text } from '@oneaudi/unified-web-components';
import styled from 'styled-components';
import type { HeadlineVariant } from '@oneaudi/unified-web-components/dist/cjs/components/headline';
import type { Dealer } from '../PartnerBusinessCardTag';
import { messages } from '../../../i18n/messages';
import { isTranslationLoaded } from '../../../lib/isTranslationLoaded';
import ImprintModal from '../ImprintModal';
import { ButtonAlignedLeft } from '../PartnerBusinessCardAddress';
import { useTrackingManager } from '../../useTrackingManager';
import type { DealerPhone, HeadlineOrder, HeadlineType, VtpDealer } from '../PartnerBusinessCard';

interface Props {
  vtpData: {
    imprint?: string;
    registrationNumber?: string;
    dealers: Dealer[];
    investorSharedVehicle: boolean;
    dynamicLabel?: string;
    vtpDealer: VtpDealer;
  };
  pbcRef: React.RefObject<HTMLDialogElement>;
  headline: {
    headlineTag?: HeadlineType;
    headlineOrder?: HeadlineOrder;
  };
}

const CustomButton = styled(ButtonAlignedLeft)`
  margin: 0 0 16px 0;
`;

const InvestorSharedContainer = styled.div`
  display: grid;
  .investor-shared-label,
  .investor-shared-info-text {
    margin: 0 0 16px 0;
  }
`;

export const InvestorSharedVehicle: React.FC<Props> = ({
  vtpData,
  pbcRef,
  headline,
}): React.ReactElement => {
  const [openDialog, setIsOpen] = React.useState(false);

  const trackingManager = useTrackingManager();

  const vtpTitle = useI18n(messages.vtpHeadline);
  const vtpInvestorSharedLabel = useI18n(messages.vtpInvestorSharedLabel);
  const showTitle = isTranslationLoaded(messages.vtpHeadline.id, vtpTitle);
  const { headlineOrder = '3', headlineTag = 'h2' } = headline;
  const nameVariant = (() => {
    if (showTitle) return { type: 'headline', order: '4', weight: 'normal' };
    return { type: 'headline', order: headlineOrder, weight: 'normal' };
  })() as HeadlineVariant;

  const nameAs = (() => {
    if (showTitle) return 'div';
    return headlineTag;
  })();
  const phoneLabel1 = useI18n(messages.vtpInvestorSharedPhoneLabel1);
  const phone = vtpData.vtpDealer.phoneNumbers?.filter(
    (dealerPhone: DealerPhone) => dealerPhone.type === 'phone',
  )[0].number;

  const hasPhone =
    phone && isTranslationLoaded(messages.vtpInvestorSharedPhoneLabel1.id, phoneLabel1);
  const originalGroupName = vtpData?.dynamicLabel as string;

  const vtpInvestorSharedPhoneLabel1 = useI18n(messages.vtpInvestorSharedPhoneLabel1);
  const vtpInvestorSharedPhoneNumber = useI18n(messages.vtpInvestorSharedPhoneNumber, {
    phoneNumber: phone as string,
  });
  const vtpInvestorSharedPhoneLabel2 = useI18n(messages.vtpInvestorSharedPhoneLabel2);
  const vtpInvestorSharedDealerChain = useI18n(messages.vtpInvestorSharedDealerChain);

  const vtpInvestorSharedLabelKey = useI18n(messages.vtpInvestorSharedLabel, {
    originalGroupName,
  });

  return (
    <InvestorSharedContainer>
      <Headline
        tag={nameAs}
        variant={nameVariant}
        data-testid="pbc-vtp-investor-shared-name"
        className="investor-shared-label"
      >
        {originalGroupName ? vtpInvestorSharedLabelKey : vtpInvestorSharedLabel}
      </Headline>
      <Text
        tag="p"
        variant={{ type: 'copy', order: '2', style: 'normal' }}
        data-testid="pbc-vtp-investor-shared-info"
        className="investor-shared-info-text"
      >
        {useI18n(messages.vtpInvestorSharedInfoText)}
      </Text>

      {hasPhone && (
        <CustomButton
          variant="standalone"
          icon="voice-call-1"
          iconPosition="leading"
          size="md"
          href={`tel:${phone}`}
          data-testid="pbc-vtp-phone"
          onClick={() => {
            trackingManager.click('call', '', 'link', 'Anrufen', '', '2', 'phone');
          }}
        >
          {`${vtpInvestorSharedPhoneLabel1} ${vtpInvestorSharedPhoneNumber} ${vtpInvestorSharedPhoneLabel2}`}
        </CustomButton>
      )}

      {vtpData?.dealers?.length > 0 && (
        <>
          <CustomButton
            variant="standalone"
            icon="address-book"
            iconPosition="leading"
            size="md"
            onClick={() => {
              setIsOpen(!openDialog);
            }}
            data-testid="pbc-imprint-trigger"
          >
            {vtpInvestorSharedDealerChain}
          </CustomButton>
          <ImprintModal
            open={openDialog}
            close={() => {
              setIsOpen(!openDialog);
            }}
            imprintRef={pbcRef}
            imprintData={vtpData?.dealers}
          />
        </>
      )}
    </InvestorSharedContainer>
  );
};
